import { Box, HStack, Text, Tooltip } from '@chakra-ui/react'
import {
  groupThreadTitleField,
  useApp,
  useThreadsList,
  useThreadTitle,
  useThreadView,
} from '@hb/react'
import { colors, MessageThread, ThreadType } from '@hb/shared'
import { useCallback, useMemo } from 'react'
import { editThreadTitleFunction } from '../../../backend/functions/other'
import { Editable } from '../../forms/Input'
import { getBadgeColor } from '../../Users/UserBadge'
import { threadAccessIcons } from '../icons'
import { ThreadMembersPopover } from '../ThreadMembers/ThreadMembersPopover'
import { THREAD_HEADER_HEIGHT } from './constants'

const ThreadTitleView = ({
  uid,
  thread,
  threadType,
  updateIsEditing,
  threadId,
}: {
  uid: string | null
  thread: MessageThread | null
  updateIsEditing: (updated: boolean) => void
  threadType: ThreadType
  threadId: string | undefined
}) => {
  const { title } = useThreadTitle(uid, threadType, threadId, thread)
  const handleSubmit = useCallback(
    async (newTitle: string) => {
      if (!threadId) return { error: 'No thread ID' }
      try {
        await editThreadTitleFunction({ type: threadType, threadId, title: newTitle })
        updateIsEditing(false)
        return { success: 'Thread title updated' }
      } catch (e: any) {
        return { error: e?.message ?? 'Failed to update thread title' }
      }
    },
    [threadId, threadType, updateIsEditing],
  )
  return (
    <Box minW="0" flex={1}>
      {threadType === ThreadType.ASSESSMENT ? (
        <Text
          color="white"
          fontFamily="Open Sans"
          fontSize="lg"
          fontWeight={500}
          px={1}
          isTruncated
          textShadow="1px 1px 3px #00000099">
          {title}
        </Text>
      ) : (
        <Editable
          previewIsButton
          value={title}
          style={{
            color: 'white',
            fontFamily: 'Open Sans',
            fontWeight: 600,
            width: '100%',
          }}
          dataCellProps={{
            color: 'white',
            fontFamily: 'Open Sans',
            fontSize: 'lg',
            isTruncated: true,
            fontWeight: 500,
            px: 1,
            textShadow: '1px 1px 3px #00000099',
          }}
          field={groupThreadTitleField}
          openCallback={() => updateIsEditing(true)}
          closeCallback={() => updateIsEditing(false)}
          onSubmit={handleSubmit}
        />
      )}
    </Box>
  )
}

export const ThreadHeader = () => {
  const { appName } = useApp()
  const {
    threadId,
    threadType,
    thread: { data: thread },
    addingMembers,
    setAddingMembers,
    editingTitle,
    setEditingTitle,
    uid,
  } = useThreadView()
  const { threadsView } = useThreadsList()

  const { accessLevel } = thread ?? {}
  const bg = useMemo(() => {
    if (threadsView === 'patient') return colors.green.hex
    if (threadType === ThreadType.ASSESSMENT) return colors.green.hex
    return getBadgeColor(appName, accessLevel)
  }, [appName, accessLevel, threadsView, threadType])

  return (
    <HStack
      borderTopRadius={6}
      // bg="linear-gradient(0deg, rgba(116,116,116,1) 0%, rgba(194,194,194,1) 100%)"
      bg={bg}
      spacing={1}
      borderBottom="1px solid #ababab"
      w="100%"
      px={3}
      height={`${THREAD_HEADER_HEIGHT}px`}
      align="center">
      {accessLevel && threadType === ThreadType.GROUP ? (
        <Tooltip
          placement="top"
          bg="whitesmoke"
          color="gray.600"
          hasArrow
          label={`${accessLevel?.toUpperCase()} CHAT`}>
          <Box
            filter="grayscale(100%) brightness(2) drop-shadow(1px 1px 2px #00000088)"
            pl="0.3rem"
            cursor="default">
            {threadAccessIcons[accessLevel] ?? ''}
          </Box>
        </Tooltip>
      ) : null}
      <ThreadTitleView
        uid={uid}
        thread={thread}
        updateIsEditing={setEditingTitle}
        threadType={threadType}
        threadId={threadId}
      />
      {editingTitle ? null : (
        <HStack spacing={1}>
          {threadType !== ThreadType.ASSESSMENT && threadType !== ThreadType.PATIENT ? (
            <ThreadMembersPopover
              threadId={threadId}
              threadType={threadType}
              isAdding={addingMembers}
              onEditClick={() => setAddingMembers(true)}
              thread={thread}
            />
          ) : null}
        </HStack>
      )}
    </HStack>
  )
}
